import React, { useState, useEffect, useContext } from 'react';
import { motion } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import Classes from './Navbar.module.css';
import useScreenWidth from '../../Hooks/useScreenWidth';
import { CartContext } from '../../Context/CartContext';
import { scrollToElement } from '../../utils/scrollToElement';

export default function Navbar({ setopenCart }) {
    const { getCartCount } = useContext(CartContext);
    const [scrolled, setScrolled] = useState(false);
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const navigateTo = useNavigate();
    const location = useLocation();
    const [activePage, setActivePage] = useState(location.pathname);
    const isMobile = useScreenWidth() < 480;
    const isWidthLess600 = useScreenWidth() < 600;

    // Determine if we should track scroll behavior (only for home page)
    const shouldTrackScroll = location.pathname === '/' || location.pathname === '*';

    // Update scroll state based on window.scrollY (only if shouldTrackScroll)
    useEffect(() => {
        if (!shouldTrackScroll) {
            setScrolled(true); // Always scrolled for non-home pages
            return;
        }

        const handleScroll = () => {
            setScrolled(window.scrollY > 50);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [shouldTrackScroll]);

    // Update windowWidth on resize
    useEffect(() => {
        const handleResize = () => setWindowWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Update active page when location changes
    useEffect(() => {
        setActivePage(location.pathname);
        // Force scrolled state for non-home pages
        if (location.pathname !== '/' && location.pathname !== '*') {
            setScrolled(true);
        }
    }, [location.pathname]);

    const handleNavigate = (page) => {
        navigateTo(page);
    };

    // This function returns a margin value based on window width and scroll state.
    const getMargin = () => {
        if (!shouldTrackScroll) {
            // For non-home pages, use the scrolled margins
            if (windowWidth < 600) return '10px 5%';
            if (windowWidth < 1000) return '10px 12%';
            if (windowWidth < 1200) return '10px 20%';
            return '10px 25%';
        }

        // For home page, use the dynamic margins based on scroll state
        if (windowWidth < 600) {
            return scrolled ? '10px 5%' : '10px 5%';
        } else if (windowWidth < 1000) {
            return scrolled ? '10px 12%' : '10px 5%';
        } else if (windowWidth < 1200) {
            return scrolled ? '10px 20%' : '10px 5%';
        } else {
            return scrolled ? '10px 25%' : '10px 8%';
        }
    };

    // Get the navbar styles based on the current state
    const getNavbarStyles = () => {
        if (!shouldTrackScroll) {
            // Styles for non-home pages (always scrolled)
            return {
                margin: getMargin(),
                padding: '0 30px',
                backdropFilter: 'blur(24px)',
                backgroundColor: 'rgba(255, 255, 255, 0.5)',
                borderRadius: '40px',
                boxShadow: 'rgba(255, 255, 255, 0.2) 0px 1px 0px 0px inset, rgba(0, 0, 0, 0.16) 0px 2px 8px 0px'
            };
        }

        // Styles for home page (dynamic based on scroll)
        return {
            margin: getMargin(),
            padding: scrolled ? '0 30px' : '0',
            backdropFilter: scrolled ? 'blur(24px)' : 'none',
            backgroundColor: scrolled ? 'rgba(255, 255, 255, 0.5)' : 'rgba(0, 0, 0, 0)',
            borderRadius: scrolled ? '40px' : '0',
            boxShadow: scrolled
                ? 'rgba(255, 255, 255, 0.2) 0px 1px 0px 0px inset, rgba(0, 0, 0, 0.16) 0px 2px 8px 0px'
                : 'none'
        };
    };

    const phoneNumber = '+971502466347';
    const whatsappURL = `https://wa.me/${phoneNumber}`;

    return (
        <motion.div
            className={Classes.navbar}
            initial={{ opacity: 0 }}
            animate={{
                opacity: 1,
                ...getNavbarStyles(),
                transform: 'none',
                transformOrigin: '50% 50% 0px',
            }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
        >
            <div style={{ display: 'flex' }}>
                <a href="/">
                    <motion.div
                        animate={{ height: shouldTrackScroll ? (scrolled ? '2rem' : '2.5rem') : '2rem' }}
                        transition={{ duration: 0.3 }}
                        className={Classes.logoCon}
                    >
                        <img src='/images/logo.png' alt='logo' style={{ height: '100%', maxHeight: '2.5rem' }} />
                        {!scrolled && (<h3>Luxaywear</h3>)}
                    </motion.div>
                </a>
            </div>
            <ul>
                <li
                    className={activePage === '/' ? Classes.navActive : ''}
                >
                    <a href="/">
                        <h5>Home</h5>
                    </a>
                </li>
                <li
                    onClick={() => { handleNavigate('/all-products') }}
                    className={activePage === '/all-products' ? Classes.navActive : ''}
                >
                    <h5>Products</h5>
                </li>
                <li
                    onClick={() => { scrollToElement('footer') }}
                    className={activePage === '/contact-us' ? Classes.navActive : ''}
                >
                    <a href={whatsappURL} target="_blank" rel="noopener noreferrer" >
                        <h5>{isMobile ? 'Contact' : 'Contact Us'}</h5>
                    </a>
                </li>
            </ul>
            <div className={Classes.gridRight}>
                <div className={Classes.cart} onClick={() => { isWidthLess600 ? navigateTo('/cart') : setopenCart(true) }}>
                    <img src='/icons/cart.png' alt='cart' draggable={false} />
                    {getCartCount() > 0 && (<h5>{getCartCount()}</h5>)}
                </div>
            </div>
        </motion.div>
    );
}